import { useCallback, useEffect, useState } from "react";
import { Archive, ArchiveRestore, Inbox, Trash2, MailWarning } from "lucide-react";
import { toast } from "sonner";
import {
  fetchAdminOrders,
  formatGBP,
  updateOrderStatus,
  updateOrderArchived,
  deleteOrder,
  sendPaymentReminders,
} from "../lib/api";
import OrdersDashboard from "../components/OrdersDashboard";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [view, setView] = useState("active"); // "active" | "archived"
  const [remindersBusy, setRemindersBusy] = useState(false);

  const loadOrders = useCallback((currentView) => {
    setLoading(true);
    fetchAdminOrders(currentView === "archived")
      .then((d) => {
        setOrders(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadOrders(view);
  }, [view, loadOrders]);

  const toggleStatus = async (order) => {
    const next = order.status === "completed" ? "awaiting_payment" : "completed";
    setUpdatingId(order.id);
    try {
      const updated = await updateOrderStatus(order.id, next);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? updated : o)));
      toast.success(
        next === "completed"
          ? `Marked ${order.order_number} as completed`
          : `Reopened ${order.order_number}`,
      );
    } catch (e) {
      toast.error("Could not update order status.");
    } finally {
      setUpdatingId(null);
    }
  };

  const toggleArchive = async (order) => {
    if (!order.archived && order.status !== "completed") {
      toast.error("Mark the order as completed before archiving.");
      return;
    }
    const next = !order.archived;
    setUpdatingId(order.id);
    try {
      await updateOrderArchived(order.id, next);
      // Remove from current list (it switches collection)
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
      toast.success(
        next
          ? `Archived ${order.order_number}`
          : `Restored ${order.order_number} to active`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update archive state.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (order) => {
    const ok = window.confirm(
      `Permanently delete order ${order.order_number}?\n\nThis cannot be undone. The customer will no longer be able to view this order.`,
    );
    if (!ok) return;
    setUpdatingId(order.id);
    try {
      await deleteOrder(order.id);
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
      toast.success(`Deleted ${order.order_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete order.");
    } finally {
      setUpdatingId(null);
    }
  };

  const isArchivedView = view === "archived";

  const runReminderSweep = async () => {
    setRemindersBusy(true);
    try {
      const r = await sendPaymentReminders();
      toast.success(
        r.sent > 0
          ? `Sent ${r.sent} payment reminder${r.sent === 1 ? "" : "s"}.`
          : `No reminders due (threshold: ${r.threshold_hours}h).`,
      );
      loadOrders(view);
    } catch (e) {
      toast.error("Could not run reminder sweep.");
    } finally {
      setRemindersBusy(false);
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <OrdersDashboard key={view + orders.length} />
      <p className="text-sm text-[#64748B] mb-6">
        Latest first · {orders.length} {isArchivedView ? "archived" : "active"} orders
      </p>

      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex border border-[#E2E8F0] w-fit">
          <button
            data-testid="tab-active"
            onClick={() => setView("active")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium tracking-wide transition-colors ${
              !isArchivedView ? "bg-[#1B3A5C] text-white" : "bg-white text-[#1B3A5C] hover:bg-[#F1F5F9]"
            }`}
          >
            <Inbox className="w-4 h-4" /> Active
          </button>
          <button
            data-testid="tab-archived"
            onClick={() => setView("archived")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium tracking-wide transition-colors border-l border-[#E2E8F0] ${
              isArchivedView ? "bg-[#1B3A5C] text-white" : "bg-white text-[#1B3A5C] hover:bg-[#F1F5F9]"
            }`}
          >
            <Archive className="w-4 h-4" /> Archived
          </button>
        </div>

        {!isArchivedView && (
          <button
            data-testid="send-reminders-btn"
            onClick={runReminderSweep}
            disabled={remindersBusy}
            className="inline-flex items-center gap-2 border border-[#2C8C9C] text-[#1B3A5C] bg-white hover:bg-[#F1F5F9] px-4 py-2.5 text-sm font-medium tracking-wide transition-colors disabled:opacity-50"
            title="Email a payment-reminder link to any awaiting-payment order older than 24h"
          >
            <MailWarning className="w-4 h-4" />
            {remindersBusy ? "Sending…" : "Send payment reminders"}
          </button>
        )}
      </div>

      <div className="border border-[#E2E8F0] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <tr>
              <th className="text-left py-3 px-4 sb-mono-label !text-[#1B3A5C]">Order</th>
              <th className="text-left py-3 px-4 sb-mono-label !text-[#1B3A5C]">Customer</th>
              <th className="text-left py-3 px-4 sb-mono-label !text-[#1B3A5C]">Items</th>
              <th className="text-right py-3 px-4 sb-mono-label !text-[#1B3A5C]">Total</th>
              <th className="text-left py-3 px-4 sb-mono-label !text-[#1B3A5C]">Status</th>
              <th className="text-right py-3 px-4 sb-mono-label !text-[#1B3A5C]">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12 text-[#64748B]">Loading…</td></tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-16 text-[#64748B]" data-testid="orders-empty">
                  {isArchivedView ? "No archived orders yet." : "No active orders."}
                </td>
              </tr>
            ) : (
              orders.map((o) => {
                const completed = o.status === "completed";
                return (
                <tr key={o.id} className="border-b border-[#E2E8F0] last:border-b-0 align-top">
                  <td className="py-3 px-4">
                    <div className="font-mono text-[#1B3A5C] font-medium">{o.order_number}</div>
                    <div className="font-mono text-xs text-[#64748B] mt-1">
                      {new Date(o.created_at).toLocaleString("en-GB")}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-medium">{o.full_name}</div>
                    <div className="text-xs text-[#64748B]">{o.email}</div>
                    <div className="text-xs text-[#64748B]">{o.phone}</div>
                    <div className="mt-2 pt-2 border-t border-[#E2E8F0] text-xs text-[#475569] leading-relaxed">
                      <div className="sb-mono-label !text-[10px] mb-1">Deliver to</div>
                      <div>{o.address_line_1}</div>
                      {o.address_line_2 ? <div>{o.address_line_2}</div> : null}
                      <div>{o.city}, {o.postcode}</div>
                      <div>{o.country}</div>
                    </div>
                    {o.notes ? (
                      <div className="mt-2 pt-2 border-t border-[#E2E8F0] text-xs text-[#475569]">
                        <div className="sb-mono-label !text-[10px] mb-1">Notes</div>
                        <div className="italic">{o.notes}</div>
                      </div>
                    ) : null}
                  </td>
                  <td className="py-3 px-4 text-xs text-[#475569]">
                    {o.items.map((i) => (
                      <div key={i.sku}>{i.name} ({i.dosage}) × {i.quantity}</div>
                    ))}
                  </td>
                  <td className="py-3 px-4 text-right font-mono font-medium text-[#1B3A5C]">
                    {formatGBP(o.total_gbp)}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      data-testid={`order-status-${o.order_number}`}
                      className={`font-mono text-xs uppercase tracking-wider px-2 py-1 border ${
                        completed
                          ? "bg-[#ECFDF5] text-[#047857] border-[#047857]/20"
                          : "bg-[#FEF2F2] text-[#B91C1C] border-[#B91C1C]/20"
                      }`}
                    >
                      {completed ? "Completed" : "Awaiting payment"}
                    </span>
                    {!completed && o.reminder_sent_at ? (
                      <div
                        data-testid={`reminder-sent-${o.order_number}`}
                        className="mt-2 font-mono text-[10px] uppercase tracking-wider text-[#64748B]"
                        title={`Reminder emailed on ${new Date(o.reminder_sent_at).toLocaleString("en-GB")}`}
                      >
                        Reminder sent · {new Date(o.reminder_sent_at).toLocaleDateString("en-GB")}
                      </div>
                    ) : null}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex flex-col items-end gap-3">
                      {!isArchivedView && (
                        <div className="flex flex-col items-end">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={completed}
                            data-testid={`order-toggle-${o.order_number}`}
                            onClick={() => toggleStatus(o)}
                            disabled={updatingId === o.id}
                            title={completed ? "Mark as awaiting payment" : "Mark as completed"}
                            className={`relative inline-flex h-6 w-11 items-center transition-colors disabled:opacity-50 ${
                              completed ? "bg-[#2C8C9C]" : "bg-[#CBD5E1]"
                            }`}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform bg-white transition-transform ${
                                completed ? "translate-x-5" : "translate-x-0.5"
                              }`}
                            />
                          </button>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-[#64748B] mt-1">
                            {completed ? "Completed" : "Mark done"}
                          </div>
                        </div>
                      )}

                      {isArchivedView ? (
                        <div className="flex flex-col items-end gap-2">
                          <button
                            type="button"
                            data-testid={`order-unarchive-${o.order_number}`}
                            onClick={() => toggleArchive(o)}
                            disabled={updatingId === o.id}
                            className="inline-flex items-center gap-1.5 border border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white px-3 py-1.5 text-xs font-medium tracking-wide transition-colors disabled:opacity-50"
                            title="Move back to active"
                          >
                            <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                          </button>
                          <button
                            type="button"
                            data-testid={`order-delete-${o.order_number}`}
                            onClick={() => handleDelete(o)}
                            disabled={updatingId === o.id}
                            className="inline-flex items-center gap-1.5 border border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white px-3 py-1.5 text-xs font-medium tracking-wide transition-colors disabled:opacity-50"
                            title="Permanently delete this order"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          data-testid={`order-archive-${o.order_number}`}
                          onClick={() => toggleArchive(o)}
                          disabled={updatingId === o.id || !completed}
                          title={completed ? "Move to archive" : "Mark order as completed first"}
                          className="inline-flex items-center gap-1.5 border border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white px-3 py-1.5 text-xs font-medium tracking-wide transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#1B3A5C]"
                        >
                          <Archive className="w-3.5 h-3.5" /> Archive
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminOrders;
